import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

type TotpSetupResponse = {
	secret: string;
	uri: string;
};

export function Setup2FA() {
	const { user, refreshUser, logout } = useAuth();
	const navigate = useNavigate();

	const [step, setStep] = useState<"init" | "verify" | "success">("init");
	const [setup, setSetup] = useState<TotpSetupResponse | null>(null);
	const [code, setCode] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const handleBeginSetup = async () => {
		setIsLoading(true);
		setError(null);
		try {
			const data = await sharedApi.post<TotpSetupResponse>("/auth/totp/setup", {});
			setSetup(data);
			setStep("verify");
		} catch {
			setError("Failed to initialize 2FA setup. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	const handleVerify = async () => {
		if (!setup) return;
		setIsLoading(true);
		setError(null);
		try {
			await sharedApi.post("/auth/totp/verify", { secret: setup.secret, code });
			setStep("success");
			await refreshUser();
			setTimeout(() => {
				navigate("/", { replace: true });
			}, 1500);
		} catch {
			setError("Invalid verification code. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="flex min-h-screen items-center justify-center bg-surface">
			<div className="w-full max-w-md">
				<div className="rounded-xl border border-line bg-surface p-8 shadow-sm">
					<div className="mb-6 text-center">
						<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-warning-soft">
							<svg
								className="h-6 w-6 text-warning"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={1.5}
								stroke="currentColor"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
								/>
							</svg>
						</div>
						<h1 className="text-xl font-bold text-content">Two-Factor Authentication Required</h1>
						<p className="mt-2 text-sm text-muted">
							{user?.role === "admin" ? "Admin" : "Editor"} accounts must enable two-factor
							authentication before accessing the dashboard.
						</p>
					</div>

					{error && (
						<div className="mb-4 rounded-lg bg-danger-soft px-4 py-3 text-sm text-danger">{error}</div>
					)}

					{step === "init" && (
						<div className="space-y-4">
							<div className="rounded-lg bg-warning-soft border border-warning/30 px-4 py-3">
								<p className="text-sm text-warning">
									You must enable two-factor authentication to continue. This adds an extra layer of
									security using a time-based one-time password (TOTP) authenticator app.
								</p>
							</div>
							<p className="text-sm text-muted">
								You will need an authenticator app such as Google Authenticator, Authy, or
								1Password.
							</p>
							<button
								type="button"
								onClick={handleBeginSetup}
								disabled={isLoading}
								className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium
									text-white transition-colors hover:bg-primary-hover
									disabled:cursor-not-allowed disabled:opacity-50"
							>
								{isLoading ? "Setting up..." : "Begin Setup"}
							</button>
						</div>
					)}

					{step === "verify" && setup && (
						<div className="space-y-5">
							<div>
								<p className="text-sm font-medium text-content">
									Step 1: Add this account to your authenticator app
								</p>
								<p className="mt-1 text-xs text-muted">
									Scan the QR code or manually enter the secret below.
								</p>
							</div>

							<div className="rounded-lg border border-line bg-surface p-3">
								<label className="mb-1 block text-xs font-medium text-muted">
									Secret key (enter manually in your app):
								</label>
								<code
									className="block break-all rounded bg-surface px-3 py-2
									font-mono text-sm text-content select-all border border-line"
								>
									{setup.secret}
								</code>
							</div>

							<div className="rounded-lg border border-line bg-surface p-3">
								<label className="mb-1 block text-xs font-medium text-muted">
									otpauth:// URI (for QR code tools):
								</label>
								<code
									className="block break-all rounded bg-surface px-3 py-2
									font-mono text-xs text-content select-all border border-line"
								>
									{setup.uri}
								</code>
							</div>

							<div>
								<p className="text-sm font-medium text-content">
									Step 2: Enter the 6-digit code from your app to verify
								</p>
								<input
									type="text"
									inputMode="numeric"
									autoComplete="one-time-code"
									required
									maxLength={6}
									value={code}
									onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
									className="mt-2 w-full rounded-lg border border-line px-3 py-2 text-center
										font-mono text-2xl tracking-[0.5em] outline-none transition-colors
										focus:border-primary focus:ring-2 focus:ring-primary/20"
									placeholder="000000"
								/>
							</div>

							<button
								type="button"
								onClick={handleVerify}
								disabled={code.length !== 6 || isLoading}
								className="w-full rounded-lg bg-success px-4 py-2.5 text-sm font-medium
									text-white transition-colors hover:bg-success
									disabled:cursor-not-allowed disabled:opacity-50"
							>
								{isLoading ? "Verifying..." : "Verify & Enable 2FA"}
							</button>
						</div>
					)}

					{step === "success" && (
						<div className="text-center space-y-3">
							<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success-soft">
								<svg
									className="h-6 w-6 text-success"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth={2}
									stroke="currentColor"
								>
									<path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
								</svg>
							</div>
							<h2 className="text-lg font-semibold text-content">2FA Enabled Successfully</h2>
							<p className="text-sm text-muted">Redirecting to dashboard...</p>
						</div>
					)}

					{step !== "success" && (
						<div className="mt-6 border-t border-line pt-4">
							<button
								type="button"
								onClick={logout}
								className="w-full text-center text-sm text-muted hover:text-muted"
							>
								Sign out
							</button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
